import React, { FormEvent, useEffect, useState } from 'react'
import { ResetFormBody, ResetFormMessage } from './style'
import {
  ResendButtonWrapper,
  ResendButton,
  ResendButtonText,
} from '../../../common/ResendButtonWrapper'
import {
  resetPassword,
  resetPasswordSubmit,
} from '../../../../../services/AuthService'
import { ConfirmationInput } from '../../../../../shared/components/confirmation-input/ConfirmationInput'
import { PasswordInput } from '../../../../../shared/components/password-input/PasswordInput'
import { PasswordConfirmInput } from '../../../../../shared/components/password-confirm-input/PasswordConfirmInput'
import { useAppDispatch, useAppSelector } from 'store/store'
import { FormTypes } from 'types/enums/EFormTypes'
import PasswordInstructions from 'pages/auth/common/PasswordInstructions'
import { useTranslation } from 'react-i18next'
import { setForm, setIsLayoutLoading } from 'store/reducers/layoutReducer'
import { setFormTranslatedText } from 'pages/auth/utils/setFormTranslatedText'
import { Button } from 'shared/components/button/Button'

export const ResetPassword: React.FC = () => {
  const dispatch = useAppDispatch()
  const email = useAppSelector((state) => state.user.email)
  const layoutEmail = useAppSelector((state) => state.layout.user.email)
  const [resetFormData, setResetFormData] = useState<{
    cofirmationCode: string
    password: string
    passwordConfirm: string
  }>({
    cofirmationCode: '',
    password: '',
    passwordConfirm: '',
  })
  const [errors, setErrors] = useState<{
    cofirmationCode?: boolean
    password?: boolean
    passwordConfirm?: boolean
  }>({
    cofirmationCode: false,
    password: false,
    passwordConfirm: false,
  })
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)

  const page = 'resetPassword'
  const { t } = useTranslation('', {
    keyPrefix: `pages.${page}`,
  })
  setFormTranslatedText(page)

  useEffect(() => {
    if (
      resetFormData.cofirmationCode &&
      resetFormData.password &&
      resetFormData.passwordConfirm &&
      !errors.cofirmationCode &&
      !errors.password &&
      !errors.passwordConfirm
    ) {
      setIsDisabled(false)
    } else if (!isDisabled) {
      setIsDisabled(true)
    }
  }, [resetFormData])

  const handleConfirmationCodeChange = (value: string, error?: boolean) => {
    setError(false)
    setErrors({ ...errors, cofirmationCode: error })
    setResetFormData({ ...resetFormData, cofirmationCode: value })
  }

  const handlePasswordChange = (value: string, error?: boolean) => {
    setError(false)
    setErrors({ ...errors, password: error })
    setResetFormData({ ...resetFormData, password: value })
  }

  const handlePasswordConfirmChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, passwordConfirm: error })
    setResetFormData({ ...resetFormData, passwordConfirm: value })
  }

  const handleSubmit = async (event: FormEvent) => {
    // dispatch(setLoading(true));
    event.preventDefault()
    try {
      dispatch(setIsLayoutLoading(true))
      await resetPasswordSubmit(
        email || layoutEmail,
        resetFormData.cofirmationCode,
        resetFormData.password,
      )
      dispatch(setForm({ formType: FormTypes.RESET_PASSWORD_SUCCESS }))
    } catch (err: unknown) {
      console.error(err)
      dispatch(setIsLayoutLoading(false))
      setError(true)
    }
  }

  const handleResendClick = async (event: FormEvent) => {
    event.preventDefault()
    resetPassword(email || layoutEmail)
  }

  return (
    <ResetFormBody>
      <>
        <ConfirmationInput
          hasError={errors.cofirmationCode}
          labelText={t('inputs.confirmation.labelText')}
          errorText={t('inputs.confirmation.errorText')}
          callback={handleConfirmationCodeChange}
        ></ConfirmationInput>
        <PasswordInstructions />
        <PasswordInput
          hasError={errors.password}
          labelText={t('inputs.password.labelText')}
          errorText={t('inputs.password.errorText')}
          callback={handlePasswordChange}
          type="new"
        ></PasswordInput>
        <PasswordConfirmInput
          hasError={errors.passwordConfirm}
          labelText={t('inputs.passwordConfirm.labelText')}
          errorText={t('inputs.passwordConfirm.errorText')}
          password={resetFormData.password}
          callback={handlePasswordConfirmChange}
        ></PasswordConfirmInput>
        {error && (
          <ResetFormMessage className="error bold">
            {t('buttons.submit.errorText')}
          </ResetFormMessage>
        )}
        <ResendButtonWrapper>
          <ResendButtonText>{t('buttons.resend.text')}</ResendButtonText>
          <ResendButton
            disabled={false}
            className={`resendButton bold`}
            text={t('buttons.resend.linkText')}
            callback={handleResendClick}
          ></ResendButton>
        </ResendButtonWrapper>
        <Button
          type="submit"
          disabled={isDisabled}
          className={`button ${isDisabled ? 'disabled' : ''}`}
          text={t('buttons.submit.text')}
          callback={handleSubmit}
        />
        <Button
          type="reset"
          disabled={false}
          text={t(`buttons.back.text`)}
          callback={() => dispatch(setForm({ formType: FormTypes.LOGIN_FORM }))}
        />
      </>
    </ResetFormBody>
  )
}
